/**
 * プロフィール選択肢定数
 *
 * FavoritesStep.tsx から移植
 */
import chroma from "chroma-js";
import { formatBytes } from "common/utils";
import { PlanType, PlanCategory } from "features/subscription";

/** プロフィールタブ */
export enum ProfileTab {
  BASIC = "basic",
  APPEARANCE = "appearance",
  FAVORITES = "favorites",
  GAME_CASE = "game_case",
}

/**
 * 現在プランから「購入可能なプラン一覧」へのマッピング。
 * entry が無い / 空配列のプラン (PREMIUM / BUILDER / EDUCATION) はアップグレードセクションを表示しない。
 */
export const AVAILABLE_UPGRADE_PLANS: Partial<Record<PlanType, PlanCategory[]>> = {
  [PlanType.TRIAL]: ["standard", "premium"],
  [PlanType.FREE]: ["standard", "premium"],
  [PlanType.STANDARD]: ["premium"],
};

/**
 * 利用状況比較リスト / プラン対比カード で共通利用する quota 項目定義。
 * key は PlanLimits の quota キーと一致させ、表示時は PLAN_LIMITS[plan][key] や usePlanLimits()[key] を参照する。
 * format = "bytes" のときはバイトを MB/GB 表示する。
 * color は利用バーのフィル色および列ヘッダーアイコン色に使用する。
 */
export type PlanFeatureKey =
  | "projects"
  | "aiChatDaily"
  | "aiImageDaily"
  | "aiImagePerRequest"
  | "assets";

export type PlanComparisonItem = {
  key: PlanFeatureKey;
  icon: string;
  labelKey: string;
  color: string;
  format?: "bytes";
  unitKey?: string;
  periodPrefixKey?: string;
};

export const PLAN_COMPARISON_ITEMS: PlanComparisonItem[] = [
  {
    key: "projects",
    icon: "apps",
    labelKey: "MSG_UPGRADE_PROMOTION_TABLE_PROJECTS",
    color: "#66BB6A",
    unitKey: "MSG_USER_PLAN_UNIT_PIECES",
  },
  {
    key: "aiChatDaily",
    icon: "chat",
    labelKey: "MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT",
    color: "#4C9DF1",
    unitKey: "MSG_USER_PLAN_UNIT_TIMES",
    periodPrefixKey: "MSG_USER_PLAN_PERIOD_DAILY",
  },
  {
    key: "aiImageDaily",
    icon: "image",
    labelKey: "MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE",
    color: "#FF9800",
    unitKey: "MSG_USER_PLAN_UNIT_TIMES",
    periodPrefixKey: "MSG_USER_PLAN_PERIOD_DAILY",
  },
  {
    key: "aiImagePerRequest",
    icon: "burst_mode",
    labelKey: "MSG_USER_PLAN_AI_IMAGE_PER_REQUEST",
    color: "#FFB74D",
    unitKey: "MSG_USER_PLAN_UNIT_SHEETS",
  },
  {
    key: "assets",
    icon: "cloud",
    labelKey: "MSG_USER_PLAN_ASSETS",
    color: "#9C27B0",
    format: "bytes",
  },
];

export type PlanComparisonSubItem = {
  key: PlanFeatureKey;
  labelKey: string;
  format?: "bytes";
  unitKey?: string;
  periodPrefixKey?: string;
};

export type PlanComparisonGroup = {
  groupKey: string;
  icon: string;
  labelKey: string;
  color: string;
  items: PlanComparisonSubItem[];
};

export type PlanComparisonSection =
  | { kind: "single"; item: PlanComparisonItem }
  | { kind: "group"; group: PlanComparisonGroup };

/**
 * UserPlanComparisonCard で利用するセクション定義。
 * AI 画像系（さくせいかいすう・どうじまいすう）はひとつのグループにまとめる。
 */
export const PLAN_COMPARISON_SECTIONS: PlanComparisonSection[] = [
  {
    kind: "single",
    item: {
      key: "projects",
      icon: "apps",
      labelKey: "MSG_UPGRADE_PROMOTION_TABLE_PROJECTS",
      color: "#66BB6A",
      unitKey: "MSG_USER_PLAN_UNIT_PIECES",
    },
  },
  {
    kind: "single",
    item: {
      key: "aiChatDaily",
      icon: "chat",
      labelKey: "MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT",
      color: "#4C9DF1",
      unitKey: "MSG_USER_PLAN_UNIT_TIMES",
      periodPrefixKey: "MSG_USER_PLAN_PERIOD_DAILY",
    },
  },
  {
    kind: "group",
    group: {
      groupKey: "aiImage",
      icon: "image",
      labelKey: "MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE",
      color: "#FF9800",
      items: [
        {
          key: "aiImageDaily",
          labelKey: "MSG_USER_PLAN_AI_IMAGE_CREATE_COUNT",
          unitKey: "MSG_USER_PLAN_UNIT_TIMES",
          periodPrefixKey: "MSG_USER_PLAN_PERIOD_DAILY",
        },
        {
          key: "aiImagePerRequest",
          labelKey: "MSG_USER_PLAN_AI_IMAGE_SIMULTANEOUS_COUNT",
          unitKey: "MSG_USER_PLAN_UNIT_SHEETS",
        },
      ],
    },
  },
  {
    kind: "single",
    item: {
      key: "assets",
      icon: "cloud",
      labelKey: "MSG_USER_PLAN_ASSETS",
      color: "#9C27B0",
      format: "bytes",
    },
  },
];

export const formatPlanLimit = (limit: number, unlimited: boolean, format?: "bytes"): string => {
  if (unlimited || limit === -1) return "∞";
  if (format === "bytes") return formatBytes(limit, 0);
  return String(limit);
};

/** ベース色タイプ */
export type BaseColorKey = "red" | "blue" | "yellow" | "green" | "white";

/** ベース色パレット（5色：四原色＋白） */
export const COLOR_PALETTE: Array<{
  key: BaseColorKey;
  labelKey: string;
  hex: string;
}> = [
  { key: "red", labelKey: "MSG_COLOR_RED", hex: "#EF4444" },
  { key: "blue", labelKey: "MSG_COLOR_BLUE", hex: "#3B82F6" },
  { key: "yellow", labelKey: "MSG_COLOR_YELLOW", hex: "#EAB308" },
  { key: "green", labelKey: "MSG_COLOR_GREEN", hex: "#22C55E" },
  { key: "white", labelKey: "MSG_COLOR_WHITE", hex: "#FFFFFF" },
];

/** 色相バリエーションのオフセット（赤・青・黄・緑用） */
const HUE_OFFSETS = [-20, -10, 0, 10, 20];

/**
 * ベース色のバリエーション5種を生成（Hex値配列）
 * @param baseKey ベース色キー
 * @returns 5つのHexカラー配列
 */
export const generateColorVariations = (baseKey: BaseColorKey): string[] => {
  const baseColor = COLOR_PALETTE.find((c) => c.key === baseKey);
  if (!baseColor) return [];

  // 白の場合はグレースケール
  if (baseKey === "white") {
    return chroma.scale(["#FFFFFF", "#737373"]).colors(5);
  }

  // その他の色は色相バリエーション
  const [h, s, l] = chroma(baseColor.hex).hsl();
  return HUE_OFFSETS.map((offset) => {
    const newHue = (h + offset + 360) % 360;
    return chroma.hsl(newHue, s, l).hex();
  });
};

/** 旧形式との互換性のための定数 */
export const FAVORITE_COLOR_OPTIONS = COLOR_PALETTE.map((c) => ({
  key: c.key,
  labelKey: c.labelKey,
  color: c.hex,
}));

/** 好きな動物選択肢 */
export const FAVORITE_ANIMAL_OPTIONS = [
  { key: "cat", icon: "pets", labelKey: "MSG_ANIMAL_CAT" },
  { key: "dog", icon: "pets", labelKey: "MSG_ANIMAL_DOG" },
  { key: "rabbit", icon: "cruelty_free", labelKey: "MSG_ANIMAL_RABBIT" },
  { key: "bear", icon: "child_care", labelKey: "MSG_ANIMAL_BEAR" },
  { key: "bird", icon: "flutter_dash", labelKey: "MSG_ANIMAL_BIRD" },
  { key: "dinosaur", icon: "skeleton", labelKey: "MSG_ANIMAL_DINOSAUR" },
];

/** 好きな食べ物選択肢 */
export const FAVORITE_FOOD_OPTIONS = [
  { key: "curry", icon: "lunch_dining", labelKey: "MSG_FOOD_CURRY" },
  { key: "ramen", icon: "ramen_dining", labelKey: "MSG_FOOD_RAMEN" },
  { key: "sushi", icon: "set_meal", labelKey: "MSG_FOOD_SUSHI" },
  { key: "hamburger", icon: "lunch_dining", labelKey: "MSG_FOOD_HAMBURGER" },
  { key: "pizza", icon: "local_pizza", labelKey: "MSG_FOOD_PIZZA" },
  { key: "icecream", icon: "icecream", labelKey: "MSG_FOOD_ICE_CREAM" },
  { key: "fruits", icon: "nutrition", labelKey: "MSG_FOOD_FRUIT" },
  { key: "cake", icon: "cake", labelKey: "MSG_FOOD_CAKE" },
];

/** 得意なこと選択肢（UserProfile用に維持） */
export const STRENGTH_OPTIONS = [
  { key: "sports", icon: "sports_soccer", labelKey: "MSG_STRENGTH_SPORTS" },
  { key: "drawing", icon: "palette", labelKey: "MSG_STRENGTH_DRAWING" },
  { key: "music", icon: "music_note", labelKey: "MSG_STRENGTH_MUSIC" },
  { key: "reading", icon: "menu_book", labelKey: "MSG_STRENGTH_READING" },
  { key: "math", icon: "calculate", labelKey: "MSG_STRENGTH_MATH" },
  { key: "games", icon: "sports_esports", labelKey: "MSG_STRENGTH_GAMES" },
  { key: "crafts", icon: "build", labelKey: "MSG_STRENGTH_CRAFTS" },
  { key: "cooking", icon: "restaurant", labelKey: "MSG_STRENGTH_COOKING" },
];

/** 興味のある分野選択肢（UserProfile用に維持） */
export const INTEREST_OPTIONS = [
  { key: "programming", icon: "code", labelKey: "MSG_INTEREST_PROGRAMMING" },
  { key: "science", icon: "science", labelKey: "MSG_INTEREST_SCIENCE" },
  { key: "art", icon: "palette", labelKey: "MSG_INTEREST_ART" },
  { key: "music", icon: "music_note", labelKey: "MSG_INTEREST_MUSIC" },
  { key: "sports", icon: "sports_soccer", labelKey: "MSG_INTEREST_SPORTS" },
  { key: "nature", icon: "park", labelKey: "MSG_INTEREST_NATURE" },
  { key: "space", icon: "rocket_launch", labelKey: "MSG_INTEREST_SPACE" },
  { key: "animals", icon: "pets", labelKey: "MSG_INTEREST_ANIMALS" },
];

/** 得意な教科選択肢 */
export const BEST_SUBJECT_OPTIONS = [
  { key: "japanese", icon: "menu_book", labelKey: "MSG_SUBJECT_LANGUAGE" },
  { key: "math", icon: "calculate", labelKey: "MSG_SUBJECT_MATH" },
  { key: "science", icon: "science", labelKey: "MSG_SUBJECT_SCIENCE" },
  { key: "social", icon: "public", labelKey: "MSG_SUBJECT_SOCIAL" },
  { key: "music", icon: "music_note", labelKey: "MSG_SUBJECT_MUSIC" },
  { key: "art", icon: "palette", labelKey: "MSG_SUBJECT_ART" },
  { key: "pe", icon: "sports_soccer", labelKey: "MSG_SUBJECT_PE" },
  { key: "life", icon: "eco", labelKey: "MSG_SUBJECT_LIFE" },
  { key: "home_ec", icon: "restaurant", labelKey: "MSG_SUBJECT_HOME_EC" },
  { key: "ethics", icon: "volunteer_activism", labelKey: "MSG_SUBJECT_ETHICS" },
  { key: "english", icon: "translate", labelKey: "MSG_SUBJECT_ENGLISH" },
  { key: "integrated", icon: "hub", labelKey: "MSG_SUBJECT_INTEGRATED" },
  {
    key: "special_activities",
    icon: "groups",
    labelKey: "MSG_SUBJECT_SPECIAL_ACTIVITIES",
  },
];

/** メガネ選択肢 */
export const GLASSES_OPTIONS = [
  { key: "none", icon: "visibility", labelKey: "MSG_GLASSES_NONE" },
  { key: "glasses", icon: "eyeglasses", labelKey: "MSG_GLASSES_YES" },
];

/** 身につけるもの選択肢 */
export const ACCESSORY_OPTIONS = [
  { key: "none", icon: "block", labelKey: "MSG_ACCESSORY_NONE" },
  { key: "megane", icon: "eyeglasses", labelKey: "MSG_ACCESSORY_MEGANE" },
  { key: "boushi", icon: "checkroom", labelKey: "MSG_ACCESSORY_BOUSHI" },
  { key: "hairpin", icon: "auto_fix_high", labelKey: "MSG_ACCESSORY_HAIRPIN" },
  { key: "ribbon", icon: "redeem", labelKey: "MSG_ACCESSORY_RIBBON" },
  { key: "bandaid", icon: "healing", labelKey: "MSG_ACCESSORY_BANDAID" },
];

/** 髪型選択肢 */
export const HAIRSTYLE_OPTIONS = [
  { key: "very_short", icon: "face", labelKey: "MSG_HAIRSTYLE_VERY_SHORT" },
  { key: "short", icon: "face", labelKey: "MSG_HAIRSTYLE_SHORT" },
  { key: "medium", icon: "face_3", labelKey: "MSG_HAIRSTYLE_MEDIUM" },
  { key: "slightly_long", icon: "face_4", labelKey: "MSG_HAIRSTYLE_SLIGHTLY_LONG" },
  { key: "long", icon: "face_4", labelKey: "MSG_HAIRSTYLE_LONG" },
];

/** 体型選択肢 */
export const BODY_TYPE_OPTIONS = [
  { key: "thin", icon: "accessibility_new", labelKey: "MSG_BODY_TYPE_THIN" },
  { key: "slightly_thin", icon: "accessibility_new", labelKey: "MSG_BODY_TYPE_SLIGHTLY_THIN" },
  { key: "medium", icon: "accessibility", labelKey: "MSG_BODY_TYPE_MEDIUM" },
  { key: "slightly_chubby", icon: "accessibility", labelKey: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY" },
  { key: "chubby", icon: "accessibility", labelKey: "MSG_BODY_TYPE_CHUBBY" },
];

/** 顔の形選択肢 */
export const FACE_SHAPE_OPTIONS = [
  { key: "round", icon: "circle", labelKey: "MSG_FACE_SHAPE_ROUND" },
  { key: "oval", icon: "egg", labelKey: "MSG_FACE_SHAPE_OVAL" },
  { key: "oblong", icon: "rectangle", labelKey: "MSG_FACE_SHAPE_OBLONG" },
  { key: "square", icon: "square", labelKey: "MSG_FACE_SHAPE_SQUARE" },
  { key: "triangle", icon: "change_history", labelKey: "MSG_FACE_SHAPE_TRIANGLE" },
];
